import { useQuery } from "@tanstack/react-query"
import { useCallback } from "react"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { ArrowRight, Building2, Loader2, LogOut } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useApiMutation } from "@/hooks/use-api-mutation"
import { useAuth } from "@/hooks/use-auth"
import type { AvailableTenant } from "@/hooks/use-auth"
import { tenantClosedMessage } from "@/lib/tenant-closed"
import type { TenantClosure } from "@/lib/tenant-closed"
import { api } from "@/services/api"
import type { AvailableTenantApi } from "@/types/api"

// Page d'un membre dont l'organisme a fermé son compte.
// Même page au rechargement (/auth/me refusé) qu'en navigation.
// Bascule proposée vers les seuls organismes encore ouverts.

/** Seuil d'organismes ouverts où un sélecteur remplace les boutons. */
const SELECT_FROM = 4

/** Appartenance servie par /auth/tenants, au format de la bascule. */
function toMembership(tenant: AvailableTenantApi, fallbackRole: string): AvailableTenant {
  return {
    id: tenant.id,
    name: tenant.name,
    slug: tenant.slug,
    role: pipe(
      O.fromNullable(tenant.role_code),
      O.getOrElse(() => fallbackRole),
    ).toLowerCase(),
    created_at: tenant.created_at,
    closed: tenant.closed,
  }
}

/** Organismes encore ouverts, au format de la bascule. */
function openMemberships(tenants: AvailableTenantApi[], fallbackRole: string): AvailableTenant[] {
  const stillOpen = tenants.filter((tenant) => !tenant.closed)
  return stillOpen.map((tenant) => toMembership(tenant, fallbackRole))
}

function OpenTenantButton({ tenant, onSwitch, isSwitching }: {
  tenant: AvailableTenant
  onSwitch: (tenant: AvailableTenant) => void
  isSwitching: boolean
}) {
  const handleClick = useCallback(() => onSwitch(tenant), [onSwitch, tenant])
  return (
    <Button
      type="button"
      variant="outline"
      className="justify-between"
      onClick={handleClick}
      disabled={isSwitching}
      data-test={`tenant-closed-switch-${tenant.slug}`}
    >
      <span className="truncate">Ouvrir {tenant.name}</span>
      <ArrowRight className="ms-2 size-4 shrink-0" aria-hidden="true" />
    </Button>
  )
}

function OpenTenantChoices({ tenants, onSwitch, isSwitching }: {
  tenants: AvailableTenant[]
  onSwitch: (tenant: AvailableTenant) => void
  isSwitching: boolean
}) {
  const handleValueChange = useCallback(
    (id: string) => {
      const chosen = O.fromNullable(tenants.find((tenant) => tenant.id === id))
      if (O.isSome(chosen)) onSwitch(chosen.value)
    },
    [tenants, onSwitch],
  )
  if (tenants.length >= SELECT_FROM) {
    return (
      <Select onValueChange={handleValueChange} disabled={isSwitching}>
        <SelectTrigger className="w-full" aria-label="Choisir un autre organisme" data-test="tenant-closed-switch-select">
          <SelectValue placeholder="Choisir un autre organisme" />
        </SelectTrigger>
        <SelectContent>
          {tenants.map((tenant) => (
            <SelectItem key={tenant.id} value={tenant.id} data-test={`tenant-closed-switch-${tenant.slug}`}>
              {tenant.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      {tenants.map((tenant) => (
        <OpenTenantButton key={tenant.id} tenant={tenant} onSwitch={onSwitch} isSwitching={isSwitching} />
      ))}
    </div>
  )
}

export function TenantClosedPage({ closure }: { closure: TenantClosure }) {
  const navigate = useNavigate()
  const { user, logout, switchTenant } = useAuth()
  const fallbackRole = pipe(
    O.fromNullable(user),
    O.match(
      () => "",
      (current) => current.role,
    ),
  )
  const tenantsQuery = useQuery({
    queryKey: ["auth-tenants"],
    queryFn: () => api.listAvailableTenants(),
    // Fermeture tout juste constatée : le cache peut la précéder.
    refetchOnMount: "always",
    retry: false,
  })
  // La bascule vide le cache et change de page.
  // Aucune liste à invalider.
  const switchMutation = useApiMutation([], (tenant: AvailableTenant) => switchTenant(tenant), {
    errorTitle: "Impossible d'ouvrir cet organisme",
  })
  const openTenants = pipe(
    O.fromNullable(tenantsQuery.data),
    O.match(
      (): AvailableTenant[] => [],
      (tenants) => openMemberships(tenants, fallbackRole),
    ),
  )

  // logout ne rejette pas. La session locale tombe toujours.
  const handleLogout = useCallback(async () => {
    await logout()
    navigate("/login")
  }, [logout, navigate])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12" data-test="tenant-closed-page">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-start gap-2 text-xl">
            <Building2 className="mt-1 size-5 shrink-0" aria-hidden="true" />
            <span data-test="tenant-closed-title">Espace fermé</span>
          </CardTitle>
          <CardDescription data-test="tenant-closed-message">{tenantClosedMessage(closure)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {tenantsQuery.isPending ? (
            <div className="flex items-center gap-2 text-muted-foreground" role="status">
              <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Recherche de vos autres organismes…
            </div>
          ) : null}
          {tenantsQuery.isError ? (
            <p className="text-muted-foreground" data-test="tenant-closed-tenants-error">
              La liste de vos autres organismes n'a pas pu être chargée.
            </p>
          ) : null}
          {openTenants.length > 0 ? (
            <div className="space-y-2" data-test="tenant-closed-switch">
              <p className="font-medium">Vous pouvez continuer dans un autre de vos organismes :</p>
              <OpenTenantChoices
                tenants={openTenants}
                onSwitch={switchMutation.mutate}
                isSwitching={switchMutation.isPending}
              />
            </div>
          ) : null}
        </CardContent>
        <CardFooter>
          <Button type="button" variant="outline" onClick={handleLogout} data-test="tenant-closed-logout">
            <LogOut className="me-2 size-4" aria-hidden="true" />
            Se déconnecter
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
